import { Association, type Organization } from "@entities";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { createPhoneNumberFilter, type HyperTableFilter } from "@shared/frontend/hyper-table";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import CustomBooleanFilter from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/filter-types/CustomBooleanFilter";
import { FormGoogle } from "clients/admin/src/desktop/components/form-components/form-google-autocomplete/FormGoogle";
import { BasicButton } from "clients/admin/src/desktop/components/generic-components/button/Button";
import { SkillsSelect } from "clients/admin/src/desktop/components/skills-select/SkillsSelect";
import { TeamsSelect } from "clients/admin/src/desktop/components/teams-select";
import dayjs from "dayjs";
import { useState } from "react";

const populationPreferencesFilter = (
  currOrganization: Organization,
): HyperTableFilter<Association> => ({
  id: "preferredPopulations",
  icon: "literal",
  caption: "אוכלוסיות מועדפות",
  renderFilter: ({ onSave, meta }) => {
    const selectedPopulationIds = (meta.selectedPopulationIds ||
      []) as string[];
    const populations = (currOrganization?.populations || [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));

    const handleChange = (event: any) => {
      const {
        target: { value },
      } = event;
      const selectedIds = typeof value === "string" ? value.split(",") : value;

      onSave({
        value: selectedIds.length
          ? Association.hyperFilter({
            user: {
              $is: {
                populationPreferences: {
                  $some: {
                    populationId: { $in: selectedIds },
                  },
                },
              },
            },
          })
          : {},
        meta: { selectedPopulationIds: selectedIds },
      });
    };

    return (
      <Select
        multiple
        value={selectedPopulationIds}
        onChange={handleChange}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(selected as string[]).map((id) => {
              const population = populations.find((p) => p.id === id);
              return <Chip key={id} label={population?.name || id} size="small" />;
            })}
          </Box>
        )}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: { sx: { borderRadius: "20px" } },
        }}
        fullWidth
      >
        {populations.map((population) => (
          <MenuItem key={population.id} value={population.id}>
            <Checkbox checked={selectedPopulationIds.includes(population.id)} />
            <ListItemText primary={population.name} />
          </MenuItem>
        ))}
      </Select>
    );
  },
});

const lastVolunteeringDateFilter: HyperTableFilter<Association> = {
  id: "lastVolunteeringDate",
  icon: "date",
  caption: "תאריך התנדבות",
  renderFilter: ({ onSave, meta }) => {
    const { startDate = null, endDate = null } = (meta as any) ?? {};

    const buildFilter = (start: Date | null, end: Date | null) => {
      if (!start && !end) return {};
      const dateFilter = {
        ...(start ? { $gte: start } : {}),
        ...(end ? { $lte: end } : {}),
      };

      return Association.hyperFilter({
        opportunities: {
          $some: {
            opportunity: {
              $is: {
                startDate: dateFilter,
              },
            },
          },
        },
      });
    };

    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
        adapterLocale="he"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginInlineStart: "1rem",
            gap: "1rem",
          }}
        >
          <DatePicker
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => {
              const newStart = date ? dayjs(date).startOf("day").toDate() : null;
              onSave({
                value: buildFilter(newStart, endDate),
                meta: { startDate: newStart, endDate },
              });
            }}
            label="מתאריך"
            reduceAnimations
            format="DD/MM/YYYY"
          />
          <DatePicker
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => {
              const newEnd = date ? dayjs(date).endOf("day").toDate() : null;
              onSave({
                value: buildFilter(startDate, newEnd),
                meta: { startDate, endDate: newEnd },
              });
            }}
            label="עד תאריך"
            reduceAnimations
            format="DD/MM/YYYY"
          />
        </div>
      </LocalizationProvider>
    );
  },
};

const toValidDate = (v: unknown): Date | null => {
  if (v == null) return null;
  const d = v instanceof Date ? v : new Date(v as string | number);
  return Number.isFinite(d.getTime()) ? d : null;
};

const makeDateRangeFilter = (
  id: string,
  caption: string,
  field: "createdAt" | "updatedAt",
): HyperTableFilter<Association> => ({
  id,
  icon: "date",
  caption,
  renderFilter: ({ onSave, meta }) => {
    const raw = (meta as any) ?? {};
    const startDate = toValidDate(raw.startDate);
    const endDate = toValidDate(raw.endDate);

    const buildFilter = (start: Date | null, end: Date | null) => {
      if (!start && !end) return {};
      return Association.hyperFilter({
        [field]: {
          ...(start ? { $gte: start } : {}),
          ...(end ? { $lte: end } : {}),
        },
      });
    };

    const handleClear = () => {
      onSave({
        value: {},
        meta: { startDate: null, endDate: null },
      });
    };

    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
        adapterLocale="he"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginInlineStart: "1rem",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: "1rem", flexWrap: "wrap" }}>
            <DateTimePicker
              value={startDate ? dayjs(startDate) : null}
              onChange={(date) => {
                const newStart = date ? dayjs(date).toDate() : null;
                onSave({
                  value: buildFilter(newStart, endDate),
                  meta: { startDate: newStart, endDate },
                });
              }}
              label="מתאריך"
              reduceAnimations
              format="DD/MM/YYYY HH:mm"
              slotProps={{ textField: { size: "small" } }}
            />
            <DateTimePicker
              value={endDate ? dayjs(endDate) : null}
              onChange={(date) => {
                const newEnd = date ? dayjs(date).toDate() : null;
                onSave({
                  value: buildFilter(startDate, newEnd),
                  meta: { startDate, endDate: newEnd },
                });
              }}
              label="עד תאריך"
              reduceAnimations
              format="DD/MM/YYYY HH:mm"
              slotProps={{ textField: { size: "small" } }}
            />
          </div>
          <BasicButton
            text="נקה סינון"
            onClick={handleClear}
            style={{
              borderRadius: "10px",
              padding: "0.3rem 1rem",
              fontSize: "small",
              alignSelf: "flex-start",
            }}
          />
        </div>
      </LocalizationProvider>
    );
  },
});

const createdAtDateFilter = makeDateRangeFilter("createdAt", "תאריך יצירה", "createdAt");
const updatedAtDateFilter = makeDateRangeFilter("updatedAt", "תאריך עריכה", "updatedAt");

const ACTIVITY_STATUSES = [
  { id: "NEW", label: "חדש" },
  { id: "ACTIVE", label: "פעיל" },
  { id: "INACTIVE", label: "לא פעיל" },
  { id: "LEGACY", label: "מאגר ישן" },
] as const;

export const getFilters = (
  currOrganization: Organization,
): HyperTableFilter<Association>[] => {
  const statuses = currOrganization.status || []; // Extract statuses from currOrganization
  return [
      {
        id: "relevantVolunteers",
        icon: "literal",
        caption: "מתנדבים רלוונטיים",
        renderFilter: ({ context, meta }) => {
          const count = Array.isArray(meta.selectedUserIds)
            ? meta.selectedUserIds.length
            : 0;
          return (
            <Stack sx={{ p: 1, minWidth: 240, gap: 1 }}>
              <Box sx={{ px: 0.5 }}>
                {typeof meta.text === "string" && meta.text
                  ? meta.text
                  : count
                    ? `${count} מתנדבים מהמשימה`
                    : "אין סינון פעיל"}
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => context.filterActions.remove("relevantVolunteers")}
              >
                הסר סינון
              </Button>
            </Stack>
          );
        },
      },
      {
        fieldName: "user.name",
        caption: "שם מלא",
        icon: "string",
        id: "name", renderFilter: ({ onSave, meta }) => {
          return (
            <div style={{ width: "90%", margin: "0 5%" }}>
              <TextField
                value={meta.value || ""}
                onChange={(e) => onSave({ value: Association.hyperFilter({ $or: [{ user: { $is: { name: { $contains: e.target.value } } } }, { personalDetails: { $is: { name: { $contains: e.target.value } } } }] }), meta: { value: e.target.value } })} />
            </div>
          );
        }
      },
      { fieldName: "user.gender", id: "gender" },
      { fieldName: "user.address", id: "address" },
      {
        id: "numberID",
        caption: "מידע לביטוח",
        icon: "boolean",
        renderFilter: ({ onSave, meta }) => (
          <CustomBooleanFilter
            value={
              ([true, false, null, undefined] as unknown[]).includes(
                meta.value,
              )
                ? ((meta.value ?? null) as any)
                : true
            }
            onChange={(updater) =>
              onSave((prev) => {
                const newValue =
                  updater instanceof Function
                    ? updater(
                      typeof prev.meta.value === "boolean"
                        ? prev.meta.value
                        : null,
                    )
                    : updater;

                if (newValue === null) {
                  return {
                    meta: {
                      value: null,
                    },
                    value: {},
                  };
                } else
                  return {
                    meta: {
                      ...prev.meta,
                      value: newValue,
                    },
                    value: Association.hyperFilter({
                      user: {
                        $is: {
                          numberID: newValue ? { $not: "" } : "",
                        },
                      },
                    }),
                  };
              })
            }
            allowUnselect
          />
        ),
      },
      {
        id: "teams",
        icon: "literal",
        caption: "פרויקטים",
        renderFilter: ({ onSave, meta }) => {
          const { teams: existTeams } = meta;
          return (
            <TeamsSelect
              id="teams-filter"
              onChange={(teams) =>
                onSave({
                  value: teams.length
                    ? Association.hyperFilter({
                      teams: {
                        $some: {
                          teamId: teams as string[],
                        },
                      },
                    })
                    : {},
                  meta: { teams },
                })
              }
              value={existTeams as string[]}
            />
          );
        },
      },
      {
        id: "skills",
        icon: "literal",
        caption: "יכולות",
        renderFilter: ({ onSave, meta }) => {
          const { skills } = meta;
          return (
            <SkillsSelect
              anchorEl={true}
              menu={false}
              showSaveButton={false}
              onChange={(skills) => {
                onSave({
                  value: skills.length
                    ? Association.hyperFilter({
                      user: {
                        $is: {
                          skills: {
                            $some: {
                              skillId: { $in: skills },
                            },
                          },
                        },
                      },
                    })
                    : {},
                  meta: { skills },
                });
              }}
              value={skills as string[]}
            />
          );
        },
      },
      {
        id: "hasVehicle",
        icon: "literal",
        caption: "רכב",
        renderFilter: ({ onSave, meta }) => {
          const checked = !!meta.checked;
          return (
            <FormControlLabel
              sx={{ px: 1 }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked;
                    onSave({
                      value: next
                        ? Association.hyperFilter({
                            user: { $is: { hasVehicle: true } },
                          })
                        : {},
                      meta: { checked: next },
                    });
                  }}
                />
              }
              label="רק מתנדבים עם רכב"
            />
          );
        },
      },
      {
        id: "activityStatus",
        icon: "literal",
        caption: "סטטוס פעילות",
        renderFilter: ({ onSave, meta }) => {
          const value = (meta.value as string) || "";
          return (
            <TextField
              select
              label="סטטוס פעילות"
              value={value}
              onChange={(e) => {
                const next = e.target.value;
                onSave({
                  value: next
                    ? Association.hyperFilter({ activityStatus: next as any })
                    : {},
                  meta: { value: next },
                });
              }}
              style={{ width: "80%", margin: "0 10%" }}
            >
              <MenuItem value="">
                <em>הכל</em>
              </MenuItem>
              {ACTIVITY_STATUSES.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          );
        },
      },
      // {
      //   id: "hasOpportunities",
      //   caption: "השתתפות במשימות",
      //   icon: "boolean",
      //   renderFilter: ({ onSave, meta }) => (
      //     <CustomBooleanFilter
      //       value={
      //         ([true, false, null, undefined] as unknown[]).includes(meta.value)
      //           ? ((meta.value ?? null) as any)
      //           : true
      //       }
      //       onChange={(updater) =>
      //         onSave((prev) => {
      //           const newValue =
      //             updater instanceof Function
      //               ? updater(
      //                   typeof prev.meta.value === "boolean"
      //                     ? prev.meta.value
      //                     : null,
      //                 )
      //               : updater;

      //           if (newValue === null) {
      //             return {
      //               meta: {
      //                 value: null,
      //               },
      //               value: {},
      //             };
      //           } else
      //             return {
      //               meta: {
      //                 ...prev.meta,
      //                 value: newValue,
      //               },
      //               value: Association.hyperFilter({
      //                 opportunities: newValue ? { $some: {} } : { $none: {} },
      //               }),
      //             };
      //         })
      //       }
      //       allowUnselect
      //     />
      //   ),
      // },
      {
        id: "status",
        icon: "literal",
        caption: "סטטוס",
        renderFilter: ({ onSave, meta }) => {
          const [metaStatuses, setMetaStatuses] = useState<string[]>(
            (meta.statuses as string[]) || [],
          ); // State for selected statuses
          const [noStatus, setNoStatus] = useState<boolean>(
            (meta.noStatus as boolean) || false,
          ); // State for "no status" checkbox

          const handleSave = () => {
            const conditions = [];
            if (metaStatuses.length) {
              conditions.push({
                status: {
                  $is: {
                    id: {
                      $in: metaStatuses.map(
                        (id) =>
                          statuses.find((status) => status.id === id)?.id,
                      ),
                    },
                  },
                },
              });
            }
            if (noStatus) {
              conditions.push({ status: { $is: null } });
            }

            onSave({
              value: conditions.length
                ? Association.hyperFilter({ $or: conditions }) // Combine conditions with $or
                : {}, // Clear filter if no conditions
              meta: { statuses: metaStatuses, noStatus },
            });
          };

          return (
            <Stack
              sx={{
                padding: "10px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                overflowY: "auto",
                height: "100%",
                paddingBottom: "4rem",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noStatus} // Check if noStatus is selected
                    onChange={(e) => setNoStatus(e.target.checked)} // Update state
                  />
                }
                label="ללא סטטוס" // Label for opportunities without a status
              />
              {statuses
                ?.filter((status) => status.type === "VOLUNTEER") // Filter by type "mission"
                .map((status) => (
                  <FormControlLabel
                    key={status.id}
                    control={
                      <Checkbox
                        checked={metaStatuses.includes(status.id)} // Check if ID is in metaStatuses
                        onChange={(e) => {
                          setMetaStatuses(
                            (prev) =>
                              e.target.checked
                                ? [...prev, status.id] // Add ID if checked
                                : prev.filter((id) => id !== status.id), // Remove ID if unchecked
                          );
                        }}
                      />
                    }
                    label={status.title}
                  />
                ))}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                style={{ marginTop: "10px" }}
              >
                שמירה
              </Button>
            </Stack>
          );
        },
      },
      createPhoneNumberFilter(Association, {
        fieldName: "user.phoneNumber",
        id: "phoneNumber",
      }),
      {
        id: "age",
        icon: "string",
        caption: "גיל",
        renderFilter: ({ onSave, meta }) => {
          const filterObj = {
            minAge: meta?.minAge || "",
            maxAge: meta?.maxAge || "",
          };

          const [state, setState] = useState(filterObj);

          const handleSave = () => {
            if (state.minAge || state.maxAge) {
              // We need to filter by dateOfBirth to calculate age
              // Create conditions array for $and at the top level
              const conditions = [];

              if (state.minAge) {
                // For minimum age: dateOfBirth should be <= (current year - minAge) December 31st
                conditions.push({
                  user: {
                    $is: {
                      dateOfBirth: {
                        $lte: new Date(new Date().getFullYear() - Number(state.minAge), 11, 31)
                      }
                    }
                  }
                });
              }

              if (state.maxAge) {
                // For maximum age: dateOfBirth should be >= (current year - maxAge - 1) January 1st
                conditions.push({
                  user: {
                    $is: {
                      dateOfBirth: {
                        $gte: new Date(new Date().getFullYear() - Number(state.maxAge) - 1, 0, 1)
                      }
                    }
                  }
                });
              }

              const filter = conditions.length === 1
                ? Association.hyperFilter(conditions[0])
                : Association.hyperFilter({ $and: conditions });

              onSave({
                value: filter,
                meta: state,
              });
            } else {
              onSave({
                value: {},
                meta: state,
              });
            }
          };

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                paddingInline: "0.5rem",
              }}
            >
              <TextField
                type="number"
                label="גיל מינימלי"
                value={state.minAge}
                onChange={(e) =>
                  setState({
                    ...state,
                    minAge: e.target.value,
                  })
                }
                inputProps={{
                  min: 0,
                  max: 120,
                }}
              />
              <TextField
                type="number"
                label="גיל מקסימלי"
                value={state.maxAge}
                onChange={(e) =>
                  setState({
                    ...state,
                    maxAge: e.target.value,
                  })
                }
                inputProps={{
                  min: 0,
                  max: 120,
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <BasicButton
                  style={{
                    borderRadius: "10px",
                    backgroundColor: "#05204c",
                    padding: "0.3rem 1rem",
                    fontWeight: 400,
                    fontSize: "small",
                  }}
                  text="שמירה"
                  onClick={handleSave}
                />
              </div>
            </div>
          );
        },
      },
      {
        id: "location",
        icon: "string",
        caption: "מרחק מכתובת",
        renderFilter: ({ onSave, meta }) => {
          const filterObj = {
            location: meta?.location || "",
            distanceKm: meta?.distanceKm || 0,
            latitude: meta?.latitude || 0,
            longitude: meta?.longitude || 0,
          };

          const [state, setState] = useState(filterObj);

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                paddingInline: "0.5rem",
              }}
            >
              <FormGoogle
                label="מיקום"
                placeValue={state.location as string}
                dir="rtl"
                onLocationChange={(location) => {
                  setState({
                    ...state,
                    location: location.locationName,
                    latitude: location.latLng.lat,
                    longitude: location.latLng.lng,
                  });
                }}
              />
              <TextField
                type="number"
                label="מרחק בקילומטרים"
                value={state.distanceKm}
                onChange={(e) =>
                  setState({
                    ...state,
                    distanceKm: Number(e.target.value) || "",
                  })
                }
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <BasicButton
                  style={{
                    borderRadius: "10px",
                    backgroundColor: "#05204c",
                    padding: "0.3rem 1rem",
                    fontWeight: 400,
                    fontSize: "small",
                  }}
                  text="שמירה"
                  onClick={() => {
                    onSave({
                      value: Association.isAddressInRadiusFromPoint({
                        radius: {
                          distance: Number(state.distanceKm),
                          measure: "km",
                        },
                        lat: state.latitude as number,
                        lng: state.longitude as number,
                      }),
                      meta: state,
                    });
                  }}
                />
              </div>
            </div>
          );
        },
      },
      {
        id: "preferredCategories",
        icon: "literal",
        caption: "העדפות התנדבות",
        renderFilter: ({ onSave, meta }) => {
          const selectedCategoryIds = (meta.selectedCategoryIds ||
            []) as string[];
          const categories = (currOrganization?.categories || [])
            .slice()
            .sort(compareBySortOrder);
          const catalogIds = new Set(categories.map((category) => category.id));
          const knownCategoryIds = selectedCategoryIds.filter((id) =>
            catalogIds.has(id),
          );

          const handleChange = (event: any) => {
            const {
              target: { value },
            } = event;
            const selectedIds = (
              typeof value === "string" ? value.split(",") : value
            ).filter((id: string) => catalogIds.has(id));

            onSave({
              value: selectedIds.length
                ? Association.hyperFilter({
                  user: {
                    $is: {
                      categoryPreferences: {
                        $some: {
                          categoryId: { $in: selectedIds },
                        },
                      },
                    },
                  },
                })
                : {},
              meta: { selectedCategoryIds: selectedIds },
            });
          };

          return (
            <Select
              multiple
              value={knownCategoryIds}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const name = categories.find((c) => c.id === id)?.name?.trim();
                    if (!name) return null;
                    return <Chip key={id} label={name} size="small" />;
                  })}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px" } },
              }}
              fullWidth
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Checkbox
                    checked={knownCategoryIds.includes(category.id)}
                  />
                  <ListItemText primary={category.name} />
                </MenuItem>
              ))}
            </Select>
          );
        },
      },
      {
        id: "comments",
        icon: "string",
        caption: "הערות לשימוש פנימי",
        renderFilter: ({ onSave, meta }) => (
          <div style={{ width: "90%", margin: "0 5%" }}>
            <TextField
              label="חיפוש בהערות"
              value={meta.value || ""}
              onChange={(e) =>
                onSave({
                  value: e.target.value
                    ? Association.hyperFilter({
                        comments: { $contains: e.target.value },
                      })
                    : {},
                  meta: { value: e.target.value },
                })
              }
            />
          </div>
        ),
      },
      populationPreferencesFilter(currOrganization),
      lastVolunteeringDateFilter,
      { fieldName: "user.email", id: "email", caption: "אימייל" },
      {
        id: "hasCommunication",
        icon: "boolean",
        caption: "תיעוד תקשורת",
        renderFilter: ({ onSave, meta }) => {
          const value = (meta.value as string) ?? "";
          return (
            <Box sx={{ p: 1, minWidth: 180 }}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={value}
                onChange={(event) => {
                  const next = event.target.value;
                  onSave({
                    value:
                      next === "yes"
                        ? Association.hyperFilter({
                            communicationRecords: { $some: {} },
                          })
                        : next === "no"
                          ? Association.hyperFilter({
                              communicationRecords: { $none: {} },
                            })
                          : {},
                    meta: {
                      value: next,
                      text: next === "yes" ? "יש תיעוד" : next === "no" ? "אין תיעוד" : "",
                    },
                  });
                }}
              >
                <MenuItem value="">הכל</MenuItem>
                <MenuItem value="yes">יש תיעוד</MenuItem>
                <MenuItem value="no">אין תיעוד</MenuItem>
              </Select>
            </Box>
          );
        },
      },
      createdAtDateFilter,
      updatedAtDateFilter,
    ];
};

export default getFilters;
